import React from 'react';
import { BaseComponentProps } from '../../types/common';
import { clsx } from 'clsx';

interface ButtonProps extends BaseComponentProps {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  className,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  type = 'button',
  onClick,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-250 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-bg-primary';
  
  const variantClasses = {
    primary: 'bg-gradient-to-r from-gold to-gold-light text-black shadow-lg hover:shadow-xl hover:from-indonesia-red hover:to-gold focus:ring-gold',
    secondary: 'bg-gradient-to-r from-gold-dark to-gold-light text-black shadow-md hover:shadow-lg hover:from-indonesia-red hover:to-gold focus:ring-gold-dark',
    ghost: 'bg-surface border border-border hover:bg-surface-hover text-text-primary focus:ring-border',
  };
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };
  
  const disabledClasses = disabled || loading
    ? 'opacity-50 cursor-not-allowed'
    : 'cursor-pointer hover:transform hover:-translate-y-0.5';
  
  const iconClasses = iconPosition === 'left' ? 'mr-2' : 'ml-2';
  
  const buttonClasses = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    disabledClasses,
    className
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-t-transparent"></div>
      )}
      
      {icon && iconPosition === 'left' && !loading && (
        <span className={iconClasses}>{icon}</span>
      )}
      
      {children}
      
      {icon && iconPosition === 'right' && (
        <span className={iconClasses}>{icon}</span>
      )}
    </button>
  );
};